// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// PDF report row + card builders extracted from app.js. These are the
// data shapers consumed by buildFlightReportPdfBlobV1 / V2 — most are
// thin wrappers that thread the app's mutable state (flightData,
// selectedFlight, healthSummary) into the *Model helpers in
// src/reporting/report-model.js. A few (reportCylinderRows,
// reportEngineDeltaMetrics, reportMultiEngine* etc.) carry real logic.
//
// Exposed as a factory so the binding stays alive against the hoisted
// helpers and the mutable state.

import { averageValues } from "../core/math.js";
import {
  reportMetricMentionableInPdf,
} from "./report-model.js";

export function createReportBuilders({
  // state getters
  getFlightData,
  getSelectedFlight,
  getSourceMonitor,
  getHealthSummary,
  getPlaybackMap,
  // model helpers (imports from src/reporting/report-model.js)
  flightReportRowsModel,
  reportTurboContextModel,
  reportEngineMetricRowsModel,
  reportHealthCheckRowsModel,
  reportTraceRowsModel,
  reportAtAGlanceTurboCardModel,
  reportAtAGlanceCardsModel,
  reportCylinderTableRowsModel,
  // formatters
  panelNumber,
  signedPanelNumber,
  scoreLabel,
  formatPointTime,
  formatElapsedTime,
  formatCompactElapsedTime,
  flightDateLabel,
  // analysis helpers
  flightDurationSeconds,
  sampleRateSummary,
  fuelUsedMetric,
  preferredPowerEstimateSummary,
  dataQualitySummary,
  estimatedHpMetric,
  engineMetricRows,
  detectionWindowsForHealthKey,
  engineMetricCylinder,
  engineWorkPoint,
  availableCylinderNumbers,
  currentEngineDescriptors,
  engineInfoForCylinderNumber,
  enginePeerKeysForCylinderNumber,
  cylinderMetricSummary,
  finiteValues,
  quantile,
  peerAverage,
  extentFromValues,
  visibleDivergenceCylinders,
  pointCylinderValues,
  hasCarbTempChannel,
  scopedHealthSummaryForEngine,
  extentMetricForEngine,
  averageMetricForEngine,
  averageEngineDelta,
  peakEngineDelta,
  engineMetricValue,
  engineDelta,
  flightProfileSummaryItems,
  phaseProfileRows,
  // report finding text helpers
  reportFindingEvidenceItems,
  reportFindingText,
  // sensor summary helpers
  sensorReviewSummaries,
  sensorSummaryEvidenceParts,
  sensorSummaryDisplayLabel,
  sensorSummaryEvidenceText,
  sensorSummaryWindowText,
  sensorSummaryReportDetail,
  // palette (getter so the binding doesn't fail at factory-call time if the
  // surrounding module hasn't bound the palette yet)
  getPdfPalette,
}) {
  function flightReportRows(summary) {
    return flightReportRowsModel(summary, {
      flightData: getFlightData(),
      selectedFlight: getSelectedFlight(),
      sourceMonitor: getSourceMonitor(),
      flightDateLabel,
      formatPointTime,
      formatElapsedTime,
      flightDurationSeconds,
      sampleRateSummary,
      fuelUsedMetric,
      preferredPowerEstimateSummary,
      dataQualitySummary,
    });
  }

  function reportTurboContext(summary) {
    return reportTurboContextModel(summary, { panelNumber, scoreLabel });
  }

  function reportEngineMetricRows(summary) {
    return reportEngineMetricRowsModel(summary, {
      engineMetricRows,
      detectionWindowsForHealthKey,
      engineMetricCylinder,
    });
  }

  function reportCylinderRows(summary) {
    const flightData = getFlightData();
    const rows = flightData.filter(engineWorkPoint);
    const analysisRows = rows.length >= 30 ? rows : flightData;
    const cylinderNumbers = availableCylinderNumbers(analysisRows);
    const engines = currentEngineDescriptors();
    const rotax = summary?.applicability?.engineFamily === "rotax";
    const sensorTop = summary.sensors?.details?.top ?? {};
    const sensorKey = summary.sensors?.score < 88 || sensorTop.suspicion >= 18 ? sensorTop.key ?? "" : "";
    const valveCylinder = summary.valve?.score < 88 ? summary.valve?.details?.top?.cylinder ?? null : null;
    const chtRhythmCylinders = summary.chtRhythm?.score < 88
      ? summary.chtRhythm?.details?.top?.cylinders ?? [summary.chtRhythm?.details?.top?.cylinder].filter(Number.isFinite)
      : [];
    const combustionCylinder = summary.combustion?.score < 88 ? summary.combustion?.details?.top?.cylinder ?? null : null;
    const divergenceCylinders = reportMetricMentionableInPdf("divergence", summary.divergence)
      ? visibleDivergenceCylinders(summary.divergence)
      : [];
    const magCylinder = reportMetricMentionableInPdf("mag", summary.mag)
      ? summary.mag?.details?.top?.weakResponses?.[0]?.cylinder ?? null
      : null;
    const coolingCylinder = summary.cooling?.score < 88 ? summary.cooling?.details?.top?.cylinder ?? null : null;
    const coolingRateCylinder = summary.coolingRate?.score < 88 ? summary.coolingRate?.details?.top?.cylinder ?? null : null;
    const hottestCylinder = summary.cht?.score < 88 ? summary.cht?.details?.hottest?.cylinder ?? null : null;
    const egtCylinder = summary.egt?.score < 88 ? summary.egt?.details?.dominant?.cylinder ?? null : null;
    const hotEgtCylinder = summary.egt?.details?.hot?.isReview ? summary.egt.details.hot.cylinder ?? null : null;

    return cylinderNumbers.map((number) => {
      const chtKey = `cht${number}`;
      const egtKey = `egt${number}`;
      const info = engineInfoForCylinderNumber(number, engines);
      const chtPeerKeys = enginePeerKeysForCylinderNumber(number, engines, "cht");
      const metric = cylinderMetricSummary(number, summary);
      const chtValues = finiteValues(analysisRows, chtKey);
      const egtValues = finiteValues(analysisRows, egtKey);
      const chtDeltaP95 = quantile(analysisRows.map((point) => {
        const value = point[chtKey];
        const peer = peerAverage(point, chtPeerKeys, chtKey);
        return Number.isFinite(value) && Number.isFinite(peer) ? value - peer : null;
      }), 0.95);
      const flags = [];
      if (sensorKey === chtKey || sensorKey === egtKey) flags.push("Probe signal");
      if (hotEgtCylinder === number) flags.push("High EGT");
      if (egtCylinder === number) flags.push("EGT spread source");
      if (valveCylinder === number) flags.push(rotax ? "EGT stability" : "EGT rhythm");
      if (chtRhythmCylinders.includes(number)) flags.push("CHT rhythm");
      if (combustionCylinder === number) flags.push("CHT/EGT pairing");
      if (divergenceCylinders.includes(number)) flags.push("Divergence");
      if (magCylinder === number) flags.push("Mag check");
      if (coolingCylinder === number) flags.push("Cooling");
      if (coolingRateCylinder === number) flags.push("Cooling rate");
      if (hottestCylinder === number) flags.push("Warmest CHT");
      return {
        number,
        label: info.label,
        metric,
        chtMax: panelNumber(extentFromValues(chtValues)?.max, " F"),
        chtP95: panelNumber(quantile(chtValues, 0.95), " F"),
        chtPeer: signedPanelNumber(chtDeltaP95, " F"),
        egtP90: panelNumber(quantile(egtValues, 0.9), " F"),
        flags,
      };
    });
  }

  function cylinderAverageForKeys(points, keys) {
    return averageValues(points.map((point) => averageValues(pointCylinderValues(point, keys))));
  }

  function cylinderSpreadAverageForKeys(points, keys) {
    return averageValues(points.map((point) => {
      const values = pointCylinderValues(point, keys);
      const extent = extentFromValues(values);
      return extent ? extent.max - extent.min : null;
    }));
  }

  function reportHealthCheckRows(summary) {
    return reportHealthCheckRowsModel(summary, { hasCarbTempChannel });
  }

  function reportTraceRows(summary) {
    return reportTraceRowsModel(summary, {
      flightData: getFlightData(),
      panelNumber,
      quantile,
      fuelUsedMetric,
      estimatedHpMetric,
    });
  }

  function reportAtAGlanceTurboCard(summary) {
    return reportAtAGlanceTurboCardModel(summary, { panelNumber });
  }

  function reportAtAGlanceCards(summary, status, primaryFinding, primaryMetric) {
    return reportAtAGlanceCardsModel(summary, status, primaryFinding, primaryMetric, {
      flightData: getFlightData(),
      panelNumber,
      quantile,
      preferredPowerEstimateSummary,
    });
  }

  function reportNormalizeEngineScopedText(value, engine) {
    if (typeof value !== "string" || !engine?.shortLabel) return value;
    return value.replace(/\b(?:F|L|R|Left|Right|Front|Rear)\s+(EGT|CHT)\s*#?\s*(\d{1,2})\b/gi, (_, family, cylinder) => (
      `${engine.shortLabel} ${String(family).toUpperCase()} ${cylinder}`
    ));
  }

  function reportNormalizeEngineFinding(value, engine) {
    if (typeof value === "string") return reportNormalizeEngineScopedText(value, engine);
    if (Array.isArray(value)) return value.map((item) => reportNormalizeEngineFinding(item, engine));
    if (value && typeof value === "object") {
      return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, reportNormalizeEngineFinding(item, engine)]));
    }
    return value;
  }

  function reportEnginePdfSummary(baseSummary, engine) {
    const summary = scopedHealthSummaryForEngine(baseSummary, engine);
    return {
      engine,
      summary,
      finding: reportNormalizeEngineFinding(summary?.finding ?? {}, engine),
      peakCht: extentMetricForEngine(engine, "cht")?.max ?? null,
      avgFuel: averageMetricForEngine(engine, "fuelFlow", { ignoreNonRunning: true }),
      avgRpm: averageMetricForEngine(engine, "rpm", { ignoreNonRunning: true }),
      oilMin: extentMetricForEngine(engine, "oilPressure", { ignoreNonRunning: true })?.min ?? null,
    };
  }

  function reportEngineDeltaMetrics(left, right, summary) {
    const baseSummary = summary ?? getHealthSummary();
    const rpmDelta = averageEngineDelta(left, right, "rpm");
    const mapDelta = averageEngineDelta(left, right, "map");
    const fuelDelta = averageEngineDelta(left, right, "fuelFlow");
    const oilDelta = averageEngineDelta(left, right, "oilPressure");
    const chtDelta = peakEngineDelta(left, right, "cht");
    const comparisonMetric = baseSummary?.engineDelta;
    const review = [
      Math.abs(rpmDelta ?? 0) >= 80,
      Math.abs(mapDelta ?? 0) >= 1.5,
      Math.abs(fuelDelta ?? 0) >= 1.5,
      Math.abs(oilDelta ?? 0) >= 10,
      (chtDelta ?? 0) >= 25,
    ].some(Boolean);
    const verdict = Number.isFinite(comparisonMetric?.score)
      ? comparisonMetric.details?.status ?? (comparisonMetric.score < 88 ? "Compare" : "Aligned")
      : review ? "Compare" : "Aligned";
    // Owner-set engine timing (Sync engine data) still compares, but the PDF
    // says so, matching the Overview Engine Delta card.
    const manualTiming = comparisonMetric?.details?.timing === "manual";
    return {
      rpmDelta,
      mapDelta,
      fuelDelta,
      oilDelta,
      chtDelta,
      label: manualTiming ? `${verdict}, manual timing` : verdict,
      score: Number.isFinite(comparisonMetric?.score) ? comparisonMetric.score : review ? 78 : 94,
    };
  }

  function engineDeltaLabel(left, right) {
    return `${right?.shortLabel ?? "R"}-${left?.shortLabel ?? "L"}`;
  }

  function reportMultiEngineCards(baseSummary, engines) {
    if ((engines?.length ?? 0) <= 1) return [];
    const [left, right] = engines;
    const leftReport = reportEnginePdfSummary(baseSummary, left);
    const rightReport = reportEnginePdfSummary(baseSummary, right);
    const delta = reportEngineDeltaMetrics(left, right, baseSummary);
    return [
      {
        label: left.label,
        value: `${leftReport.summary?.overall?.score ?? "--"}/100`,
        detail: leftReport.finding?.title ?? leftReport.summary?.overall?.label ?? "Engine review",
        score: leftReport.summary?.overall?.score,
      },
      {
        label: right.label,
        value: `${rightReport.summary?.overall?.score ?? "--"}/100`,
        detail: rightReport.finding?.title ?? rightReport.summary?.overall?.label ?? "Engine review",
        score: rightReport.summary?.overall?.score,
      },
      {
        label: "Engine Delta",
        value: delta.label,
        detail: `${engineDeltaLabel(left, right)} RPM ${signedPanelNumber(delta.rpmDelta)}, fuel ${signedPanelNumber(delta.fuelDelta, " GPH", 1)}`,
        score: delta.score,
      },
    ];
  }

  function reportMultiEngineOverviewRows(baseSummary, engines) {
    if ((engines?.length ?? 0) <= 1) return [];
    const [left, right] = engines;
    const reports = [left, right].map((engine) => reportEnginePdfSummary(baseSummary, engine));
    const delta = reportEngineDeltaMetrics(left, right, baseSummary);
    return [
      ...reports.map((item) => [
        item.engine.label,
        `${item.summary?.overall?.score ?? "--"}/100`,
        item.finding?.title ?? item.summary?.overall?.label ?? "Engine review",
        panelNumber(item.peakCht, " F"),
        panelNumber(item.avgFuel, " GPH", 1),
        panelNumber(item.avgRpm),
        panelNumber(item.oilMin, " psi"),
      ]),
      [
        `${engineDeltaLabel(left, right)} average`,
        delta.label,
        `MAP ${signedPanelNumber(delta.mapDelta, " inHg", 1)}`,
        panelNumber(delta.chtDelta, " F"),
        signedPanelNumber(delta.fuelDelta, " GPH", 1),
        signedPanelNumber(delta.rpmDelta),
        signedPanelNumber(delta.oilDelta, " psi"),
      ],
    ];
  }

  function reportMultiEnginePrimaryCues(baseSummary, engines) {
    if ((engines?.length ?? 0) <= 1) return [];
    return engines.slice(0, 2).map((engine) => {
      const report = reportEnginePdfSummary(baseSummary, engine);
      const metric = report.summary?.overall;
      return {
        label: `${engine.label} Primary Cue`,
        title: report.finding?.title ?? "Engine review",
        score: metric?.score,
        evidenceItems: reportFindingEvidenceItems(report.finding, report.finding?.meta ?? metric?.note ?? ""),
        body: reportFindingText(report.finding, 330),
        causes: report.finding?.causes ?? [],
        actions: report.finding?.actions ?? [],
      };
    });
  }

  function reportMultiEngineTraceRows(engines) {
    if ((engines?.length ?? 0) <= 1) return [];
    const [left, right] = engines;
    const flightData = getFlightData();
    const pdfPalette = getPdfPalette();
    const metricValues = (engine, metric) => flightData.map((point) => engineMetricValue(point, engine, metric));
    const deltaValues = (metric) => flightData.map((point) => engineDelta(point, left, right, metric));
    return [
      {
        label: `${left.shortLabel} CHT avg`,
        values: metricValues(left, "cht"),
        suffix: " F",
        color: pdfPalette.amber,
        summary: `peak ${panelNumber(extentMetricForEngine(left, "cht")?.max, " F")}`,
      },
      {
        label: `${right.shortLabel} CHT avg`,
        values: metricValues(right, "cht"),
        suffix: " F",
        color: pdfPalette.red,
        summary: `peak ${panelNumber(extentMetricForEngine(right, "cht")?.max, " F")}`,
      },
      {
        label: `CHT ${engineDeltaLabel(left, right)}`,
        values: deltaValues("cht"),
        suffix: " F",
        color: pdfPalette.purple,
        summary: `peak abs ${panelNumber(peakEngineDelta(left, right, "cht"), " F")}`,
      },
      {
        label: `${left.shortLabel} fuel flow`,
        values: metricValues(left, "fuelFlow"),
        suffix: " GPH",
        digits: 1,
        color: pdfPalette.teal,
        summary: `avg ${panelNumber(averageMetricForEngine(left, "fuelFlow", { ignoreNonRunning: true }), " GPH", 1)}`,
      },
      {
        label: `${right.shortLabel} fuel flow`,
        values: metricValues(right, "fuelFlow"),
        suffix: " GPH",
        digits: 1,
        color: pdfPalette.green,
        summary: `avg ${panelNumber(averageMetricForEngine(right, "fuelFlow", { ignoreNonRunning: true }), " GPH", 1)}`,
      },
      {
        label: `Fuel ${engineDeltaLabel(left, right)}`,
        values: deltaValues("fuelFlow"),
        suffix: " GPH",
        digits: 1,
        color: pdfPalette.blue,
        summary: `avg ${signedPanelNumber(averageEngineDelta(left, right, "fuelFlow"), " GPH", 1)}`,
      },
    ];
  }

  function reportCylinderTableRows(cylinderRows) {
    return reportCylinderTableRowsModel(cylinderRows);
  }

  async function reportFlightMapImage() {
    const flightData = getFlightData();
    const playbackMap = getPlaybackMap();
    if (!playbackMap?.hasTrack?.(flightData)) return null;
    try {
      return await playbackMap.createStaticImage(flightData, { width: 1100, height: 480 });
    } catch (error) {
      console.warn("Could not render flight map for PDF", error);
      return null;
    }
  }

  function reportFlightMapCaption(mapImage) {
    const points = Number.isFinite(mapImage?.pointCount) ? `${mapImage.pointCount.toLocaleString()} GPS samples` : "GPS samples";
    const start = mapImage?.start ? formatCompactElapsedTime(mapImage.start.elapsedSeconds) : "start";
    const end = mapImage?.end ? formatCompactElapsedTime(mapImage.end.elapsedSeconds) : "end";
    return `OpenStreetMap flight track, ${points}, ${start} to ${end}.`;
  }

  function reportFlightDistanceLabel(mapImage) {
    const distance = mapImage?.distanceNm;
    if (!Number.isFinite(distance) || distance <= 0) return "Not available";
    return `${distance >= 100 ? Math.round(distance).toLocaleString() : distance.toFixed(1)} NM`;
  }

  function reportFlightProfileRows(data, phases, options = {}) {
    const flightData = data ?? getFlightData();
    const phaseRows = phases ?? phaseProfileRows(flightData);
    const rows = flightProfileSummaryItems(flightData, phaseRows)
      .filter(([label]) => label !== "Time range");
    const mapDistanceLabel = options.mapImage ? reportFlightDistanceLabel(options.mapImage) : null;
    return rows.map(([label, value]) => (
      label === "Distance travelled" && mapDistanceLabel
        ? [label, mapDistanceLabel]
        : [label, value]
    ));
  }

  function reportFlightMapTiles(mapImage, data, phases) {
    const flightData = data ?? getFlightData();
    const phaseRows = phases ?? phaseProfileRows(flightData);
    return reportFlightProfileRows(flightData, phaseRows, { mapImage });
  }

  function reportSensorChannelItems(metric, limit = 5) {
    return sensorReviewSummaries(metric)
      .filter((summary) => sensorSummaryEvidenceParts(summary).length > 0)
      .slice(0, limit)
      .map((summary) => ({
        label: sensorSummaryDisplayLabel(summary),
        evidence: sensorSummaryEvidenceText(summary, 3),
        window: sensorSummaryWindowText(summary),
        detail: sensorSummaryReportDetail(summary),
      }));
  }

  const reportActionPriorityRank = { high: 0, medium: 1, low: 2 };
  function sortReportActionsByPriority(actions = []) {
    return actions
      .slice()
      .sort(([a], [b]) => (reportActionPriorityRank[a] ?? 99) - (reportActionPriorityRank[b] ?? 99));
  }

  return {
    flightReportRows,
    reportTurboContext,
    reportEngineMetricRows,
    reportCylinderRows,
    cylinderAverageForKeys,
    cylinderSpreadAverageForKeys,
    reportHealthCheckRows,
    reportTraceRows,
    reportAtAGlanceTurboCard,
    reportAtAGlanceCards,
    reportNormalizeEngineScopedText,
    reportNormalizeEngineFinding,
    reportEnginePdfSummary,
    reportEngineDeltaMetrics,
    engineDeltaLabel,
    reportMultiEngineCards,
    reportMultiEngineOverviewRows,
    reportMultiEnginePrimaryCues,
    reportMultiEngineTraceRows,
    reportCylinderTableRows,
    reportFlightMapImage,
    reportFlightMapCaption,
    reportFlightDistanceLabel,
    reportFlightProfileRows,
    reportFlightMapTiles,
    reportSensorChannelItems,
    sortReportActionsByPriority,
  };
}
